import { createContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("cart");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems((prev) => {
      const existingItem = prev.find(
        (i) => i.proId === item.proId && i.subId === item.subId
      );
      if (existingItem) {
        return prev.map((i) =>
          i.proId === item.proId && i.subId === item.subId
            ? { ...i, quantity: item.quantity }
            : i
        );
      }
      return [...prev, item];
    });
  };

  const addOneToCart = (proId, subId) => {
    setCartItems((prev) => {
      const existingItem = prev.find(
        (i) => i.proId === proId && i.subId === subId
      );
      if (existingItem) {
        return prev.map((i) =>
          i.proId === proId && i.subId === subId
            ? { ...i, quantity: (i.quantity || 0) + 1 }
            : i
        );
      }
      return prev; // Do nothing if item not found
    });
  };

  const removeOneFromCart = (proId, subId) => {
    setCartItems((prev) => {
      const existingItem = prev.find(
        (i) => i.proId === proId && i.subId === subId
      );
      if (existingItem && existingItem.quantity > 1) {
        return prev.map((i) =>
          i.proId === proId && i.subId === subId
            ? { ...i, quantity: i.quantity - 1 }
            : i
        );
      }
      return prev.filter((i) => !(i.proId === proId && i.subId === subId));
    });
  };

  const removeFromCart = (proId, subId) => {
    setCartItems((prev) =>
      prev.filter((i) => !(i.proId === proId && i.subId === subId))
    );
  };

  const calculateOverallTotals = () => {
    const netTotal = Math.round(
      cartItems.reduce((sum, item) => sum + (item.rate || 0) * (item.quantity || 0), 0)
    );
    const youSave = Math.round(
      cartItems.reduce(
        (sum, item) => sum + ((item.rate || 0) * (item.quantity || 0) * (item.discount || 0) / 100),
        0
      )
    );
    const overallTotal = Math.round(
      cartItems.reduce(
        (sum, item) => sum + (item.rate || 0) * (item.quantity || 0) * (1 - (item.discount || 0) / 100),
        0
      )
    );
    return { netTotal, youSave, overallTotal };
  };

  const calculateItemTotals = (rate, quantity, discount) => {
    const originalPrice = (rate || 0) * (quantity || 0);
    const discountedPrice = originalPrice * (1 - (discount || 0) / 100);
    return {
      originalPrice: Math.round(originalPrice),
      discountedPrice: Math.round(discountedPrice),
    };
  };

  const updateCartItemQty = (proId, subId, qty) => {
    setCartItems((prev) => {
      const parsedQty = parseInt(qty);
      if (isNaN(parsedQty) || parsedQty <= 0) {
        return prev.filter((i) => !(i.proId === proId && i.subId === subId));
      }
      return prev.map((i) =>
        i.proId === proId && i.subId === subId
          ? { ...i, quantity: parsedQty }
          : i
      );
    });
  };

  const [wishlist, setWishlist] = useState(() => {
    try {
      const saved = localStorage.getItem("wishlist");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [isWishlistOpen, setIsWishlistOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const toggleWishlist = (subId) => {
    setWishlist((prev) =>
      prev.includes(subId) ? prev.filter((id) => id !== subId) : [...prev, subId]
    );
  };

  // Delivery region state - fetched ONLY from backend admin settings
  const [selectedRegion, setSelectedRegion] = useState("");
  const [minOrderAmounts, setMinOrderAmounts] = useState({});

  // Fetch delivery regions from backend on mount
  useEffect(() => {
    axios.get(`${API_BASE_URL}/delivery-regions`)
      .then((res) => {
        if (res.data && typeof res.data === 'object' && Object.keys(res.data).length > 0) {
          setMinOrderAmounts(res.data);
        }
      })
      .catch((err) => {
        console.log("No delivery regions from backend, using empty set:", err.message);
      });
  }, []);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        addOneToCart,
        removeOneFromCart,
        removeFromCart,
        updateCartItemQty,
        calculateOverallTotals,
        calculateItemTotals,
        searchTerm,
        setSearchTerm,
        selectedCategory,
        setSelectedCategory,
        wishlist,
        toggleWishlist,
        isWishlistOpen,
        setIsWishlistOpen,
        selectedRegion,
        setSelectedRegion,
        minOrderAmounts
      }}
    >
      {children}
    </CartContext.Provider>
  );
};